/** Simple observable store for app state. */

import type { InsightsSummaryResponse, Account, TrendsResponse } from '../api/types.js';
import type { ApiError } from '../api/client.js';
import type { CurrentUser } from '../auth/me-fetcher.js';

export type SheetCategory = 'Essentials' | 'Lifestyle' | 'Wealth';

export type AuthStatus = 'unknown' | 'logged-in' | 'logged-out';

export interface AppState {
  currentMonth: string;
  currentView: 'summary' | 'ask' | 'briefing' | 'onboarding' | 'welcome';
  /**
   * Optional query-string `source` for the current view. Set by the router
   * when the hash contains `?source=invite`. Read by the welcome view to
   * branch into the invitee variant (TASK-071 / TASK-072).
   */
  viewSource: string | null;
  insightsData: InsightsSummaryResponse | null;
  insightsCache: Record<string, InsightsSummaryResponse>;
  isLoading: boolean;
  error: ApiError | null;
  accounts: Account[];
  settingsPanelOpen: boolean;
  openSheet: SheetCategory | null;
  sheetLoading: boolean;
  trendsCache: Record<string, TrendsResponse>;
  authStatus: AuthStatus;
  currentUser: CurrentUser | null;
  /**
   * Wall-clock deadline (ms since epoch) until which the post-link backfill
   * poller is expected to be pulling additional Plaid history. While this is
   * set and in the future, the summary view auto-refreshes every 60s and
   * shows a subtle "Loading more history…" indicator. Persisted in
   * localStorage scoped by user id (see persistBackfillUntilMs).
   */
  backfillUntilMs: number | null;
}

export type Listener = (state: AppState, prev: AppState) => void;
type Unsubscribe = () => void;

function getDefaultMonth(): string {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

function createInitialState(): AppState {
  return {
    currentMonth: getDefaultMonth(),
    currentView: 'summary',
    viewSource: null,
    insightsData: null,
    insightsCache: {},
    isLoading: false,
    error: null,
    accounts: [],
    settingsPanelOpen: false,
    openSheet: null,
    sheetLoading: false,
    trendsCache: {},
    authStatus: 'unknown',
    currentUser: null,
    backfillUntilMs: null,
  };
}

let state: AppState = createInitialState();
const listeners = new Set<Listener>();

export function getState(): AppState {
  return state;
}

export function setState(partial: Partial<AppState>): void {
  const prev = state;
  state = { ...state, ...partial };
  for (const listener of listeners) {
    listener(state, prev);
  }
}

export function subscribe(listener: Listener): Unsubscribe {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Reset store to initial state (for testing). */
export function resetStore(): void {
  state = createInitialState();
  listeners.clear();
}

// ── Feature-flag selectors (plan §H3b) ───────────────────────────────────
// Centralize the fail-closed evaluation here so every consumer agrees on
// "what does feature-on look like" — undefined currentUser, undefined
// `feature_flags`, and `user_categorization: false` ALL collapse to false.
// Backend is the source of truth; this just mirrors `resolveFeatureFlags`.

export function isUserCategorizationEnabled(): boolean {
  return state.currentUser?.feature_flags?.user_categorization === true;
}

// ── Backfill deadline persistence ─────────────────────────────────────────
// Scoped by user id so a logged-out user (or a different user on the same
// browser) cannot inherit a stale "loading more history…" indicator from a
// previous session.

const BACKFILL_LS_PREFIX = 'fc.backfillUntilMs:';

function backfillKey(userId: string): string {
  return `${BACKFILL_LS_PREFIX}${userId}`;
}

function safeStorage(): Storage | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage;
  } catch {
    return null;
  }
}

/** Read the persisted backfill deadline for this user, if any. */
export function loadBackfillUntilMs(userId: string): number | null {
  const ls = safeStorage();
  if (!ls) return null;
  const raw = ls.getItem(backfillKey(userId));
  if (!raw) return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return n;
}

/**
 * Persist the backfill deadline for this user and update store state. The
 * effective value is `max(existing, deadline)` so a fresh link doesn't
 * shorten an in-progress chain from a previous link.
 */
export function persistBackfillUntilMs(userId: string, deadlineMs: number): void {
  const existing = loadBackfillUntilMs(userId) ?? 0;
  const value = Math.max(existing, deadlineMs);
  const ls = safeStorage();
  if (ls) {
    try {
      ls.setItem(backfillKey(userId), String(value));
    } catch {
      // Quota errors and disabled storage are non-fatal; the in-memory
      // state alone still drives the auto-refresh for the current session.
    }
  }
  setState({ backfillUntilMs: value });
}

/** Clear backfill state for this user (e.g. on logout). */
export function clearBackfillState(userId: string | null): void {
  if (userId) {
    const ls = safeStorage();
    if (ls) {
      try {
        ls.removeItem(backfillKey(userId));
      } catch {
        // Ignore.
      }
    }
  }
  setState({ backfillUntilMs: null });
}
